import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { AviionHttpClient } from '@core/http';
import { SkinningService } from '@core/skin';

import { IMenuItemDto, IMenuResponseDto } from './menu-api.dto';
import { IMenuEntry } from './menu.models';

// Boundary for the V1 menu endpoint: GET /api/v1/menu → top-level nav entries.
// HTTP only — no caching, no state. Caller (MenuStore) decides when to fetch
// and where to put the result.

@Injectable({ providedIn: 'root' })
export class MenuApiService {
    private readonly http = inject(AviionHttpClient);
    private readonly skinning = inject(SkinningService);

    public getMenu(): Observable<IMenuEntry[]> {
        return this.http.get_2('api/v2/menu').pipe(
            map(res => ((res as IMenuResponseDto).items ?? []).map(dto => this.toEntry(dto))),
        );
    }

    private toEntry(dto: IMenuItemDto): IMenuEntry {
        const route = routeFor(dto.tileType, dto.contentIdentifier);
        return {
            id: stableId(dto),
            url: route.url,
            icon: dto.imageUrl?.length ? dto.imageUrl : this.skinning.getImage(route.iconKey),
            title: dto.title?.trim() ?? '',
            active: false,
            testId: legacyTestId(dto.tileType, dto.contentIdentifier),
        };
    }
}

// Legacy nav names from the pre-rewrite homepage.service mapMenuItems — the QA
// suite selects header items via `header_menu-{name}` (e.g. header_menu-vod),
// so these must not drift even though routing now keys on stableId().
function legacyTestId(tileType: number, contentIdentifier: string | null): string {
    switch (tileType) {
        case 0:  return 'videostore';
        case 1:  return 'radio';
        case 2:  return 'editors_choice';
        case 3:  return 'recommendations';
        case 4:  return 'favourites';
        case 7:  return 'recordings';
        case 8:
        case 15: return 'live_tv';
        case 9:  return contentIdentifier ?? 'vod';
        // 10 (legacy watch_later) and 24 both land on my-content and share this name,
        // so a menu carrying both renders two `header_menu-my_content` entries.
        case 10: return 'my_content';
        case 11: return 'messages';
        case 13: return 'recently_watched';
        case 14: return 'guide';
        case 24: return 'my_content';
        default: return `tile-${tileType}`;
    }
}

// Two top-level items can share the same numeric `id` (e.g. tileType 9 with
// contentIdentifier 'vod' AND 'aod'). Combine both so each nav entry has a
// stable, unique key for tracking and active-state lookups.
function stableId(dto: IMenuItemDto): string {
    const tile = dto.tileType;
    const cid = dto.contentIdentifier ?? '';
    return cid ? `tile-${tile}-${cid}` : `tile-${tile}`;
}

// Mirrors the legacy mapMenuItems mapping — tileType (and contentIdentifier
// for tile 9) determines the in-app route + the fallback skinning icon key.
// Exceptions to "legacy": tile 9 (see decision C below) and tile 10, which now
// opens my-content instead of the retired standalone watch_later page.
function routeFor(tileType: number, contentIdentifier: string | null): { url: string; iconKey: string } {
    switch (tileType) {
        case 0:  return { url: 'videostore',     iconKey: 'icon_tile_videostore' };
        case 1:  return { url: 'live/radio',     iconKey: 'icon_tile_radio' };
        case 2:  return { url: 'editors_choice', iconKey: 'icon_tile_editors_choice' };
        case 3:  return { url: 'recommendations',iconKey: 'icon_tile_recommended' };
        case 4:  return { url: 'live/favorites', iconKey: 'icon_tile_favorites' };
        case 7:  return { url: 'live/recordings',iconKey: 'icon_tile_recordings' };
        case 8:
        case 15: return { url: 'live/mosaic',     iconKey: 'icon_tile_nowontv' };
        // Every tile-9 item now opens the dynamic V3 catalogue page keyed by its
        // contentIdentifier — no more hardcoded vod/aod landings (see task.md, decision C).
        case 9:
            return { url: `videostore/catalogue/${encodeURIComponent(contentIdentifier ?? '')}`, iconKey: 'icon_tile_videostore' };
        // Tile 10 was the standalone watch_later page; it now shares the my-content
        // route with tile 24. The icon key stays watch-later so operators that skin
        // the two entries differently keep their existing artwork.
        case 10: return { url: 'my-content',    iconKey: 'icon_tile_watch_later' };
        case 11: return { url: 'messages',       iconKey: 'icon_tile_messages' };
        case 13: return { url: 'recently_watched', iconKey: 'icon_tile_recently_watched' };
        case 14: return { url: 'live/programme', iconKey: 'icon_tile_guide' };
        case 24: return { url: 'my-content',     iconKey: 'icon_my_content' };
        default: return { url: '', iconKey: '' };
    }
}
